import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import themeColors, { themeFontSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

/* ---------------------------------------------
   STYLES
---------------------------------------------- */

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    width: 100%;

    @media (min-width: 768px) {
        flex-direction: row;
        justify-content: center;
        align-items: flex-start;
    }
`;

const ControlGroup = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 6px;

    @media (min-width: 768px) {
        gap: 10px;
    }

    label {
        color: ${themeColors.colorCharcoalGrey};
        font-family: Roboto, sans-serif;
        font-size: ${themeFontSizes.size14};
        min-width: 120px;
        display: inline-block;
    }
`;

const PageInput = styled.input.attrs({ type: "number", min: 1 }) <{ hasError: boolean }>`
    width: 70px;
    padding: 6px 8px;
    border: 1px solid ${({ hasError }) => (hasError ? "#C62828" : themeColors.colorBorderBrands)};
    border-radius: 2px;
    text-align: center;
    font-family: Roboto, sans-serif;
    font-size: ${themeFontSizes.size14};

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    -moz-appearance: textfield;
`;

const ErrorMessage = styled.div`
    color: #C62828;
    font-size: 0.85rem;
    margin-top: 4px;
    font-weight: bold;
    font-style: italic;
    min-height: 18px;
    text-align: center;
`;

/* ---------------------------------------------
   MOBIUS SELECT STYLES
---------------------------------------------- */

export const sortSelectStyles: { select?: SelectPresentationProps } = {
    select: {
        labelProps: {
            css: css`
                width: unset;
                color: ${themeColors.colorCharcoalGrey};
                font-family: Roboto, sans-serif;
                font-size: ${themeFontSizes.size14};
                letter-spacing: 0.01rem;
                font-weight: normal;
            `,
        },
        cssOverrides: {
            formField: css`
                width: inherit;
                justify-content: right;

                > div {
                    width: auto;
                    min-width: 11.5rem;

                    @media screen and (min-width: 768px) and (max-width: 1200px) {
                        min-width: initial;
                    }
                }

                select {
                    border: 1px solid ${themeColors.colorBorderBrands};
                    border-radius: 0.125rem;
                    background-color: ${themeColors.colorWhite};
                    color: ${themeColors.colorCharcoalGrey};
                    font-family: Roboto, sans-serif;
                    font-size: ${themeFontSizes.size14};
                    letter-spacing: 0.01rem;
                    line-height: 1.125rem;

                    &:focus {
                        outline: rgb(32, 96, 173) dashed 2px !important;
                    }
                }
            `,
        },
    },
};

const styles = sortSelectStyles;

/* ---------------------------------------------
   COMPONENT
---------------------------------------------- */

const ProductListSortSelect: FC<Props> = ({ addProductFilters, pagination }) => {
    const [pageInput, setPageInput] = useState("");
    const [error, setError] = useState("");

    if (!pagination) return null;

    const totalPages = Math.ceil(pagination.totalItemCount / pagination.pageSize);

    /* ------------------------------
       SORT OPTION REORDERING
    ------------------------------ */
    const sortOptions = pagination.sortOptions;
    const rental = sortOptions.find(o => o.displayName === "Rental");
    const used = sortOptions.find(o => o.displayName === "Used");

    const reorderedSortOptions = sortOptions.filter(
        o => o.displayName !== "Rental" && o.displayName !== "Used"
    );

    const bestMatchIndex = reorderedSortOptions.findIndex(
        o => o.displayName === "Best Match"
    );

    if (rental && used && bestMatchIndex !== -1) {
        reorderedSortOptions.splice(bestMatchIndex + 1, 0, rental, used);
    }

    /* ------------------------------
       JUMP TO PAGE LOGIC
    ------------------------------ */

    const changePage = (page: number) => {
        addProductFilters({ page });
        window.scrollTo(0, 0);
    };

    const handleJump = (value?: string) => {
        const inputValue = value ?? pageInput;

        if (!inputValue) {
            setError("Please enter a valid page number.");
            return;
        }

        if (!/^[1-9][0-9]*$/.test(inputValue)) {
            setError("Please enter a valid positive page number.");
            return;
        }

        let pageNum = Number(inputValue);

        if (pageNum < 1 || pageNum > totalPages) {
            setError("Page number not available, redirecting to nearest match.");
            pageNum = Math.min(Math.max(pageNum, 1), totalPages);
            changePage(pageNum);
            setPageInput(pageNum.toString());
            return;
        }

        setError("");
        changePage(pageNum);
        setPageInput(pageNum.toString());
    };

    return (
        <Wrapper>
            {/* Jump to Page */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ControlGroup>
                    <label htmlFor="jumpToPage">{translate("Jump to page")}</label>
                    <PageInput
                        id="jumpToPage"
                        value={pageInput}
                        hasError={!!error}
                        onChange={(e) => {
                            setPageInput(e.target.value);
                            setError("");
                        }}
                        onKeyDown={(e) => {
                            const value = (e.target as HTMLInputElement).value;

                            if (e.key === "Enter") {
                                handleJump(value);
                            }

                            if (e.key === "Tab") {
                                setTimeout(() => handleJump(value), 0);
                            }
                        }}
                        onBlur={(e) => handleJump(e.target.value)}
                    />
                </ControlGroup>

                <ErrorMessage>{error}</ErrorMessage>
            </div>

            {/* Sort By (Mobius Select) */}
            <Select
                {...styles.select}
                label={translate("Sort by")}
                value={pagination.sortType}
                labelPosition="left"
                onChange={(e) => addProductFilters({ sort: e.currentTarget.value })}
                data-test-selector="productListSortSelect"
            >
                {reorderedSortOptions.map(option => (
                    <option key={option.sortType} value={option.sortType}>
                        {option.displayName}
                    </option>
                ))}
            </Select>
        </Wrapper>
    );
};

/* ---------------------------------------------
   WIDGET MODULE
---------------------------------------------- */

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSortSelect),
    definition: {
        group: "Product List",
        displayName: "Sort Select (with Jump to Page)",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;
